// pages/SAPWorkbench.jsx — Tab "Custom Workbench" di dalam ABAP Source Viewer.
// Inventaris objek Z*/Y* dari TADIR + penarikan source massal.
//
// Bentuknya POHON, bukan dua tabel terpisah: tipe objek → objek → isi. Alasannya
// sederhana, isi tabel bawah dulu selalu harus dicocokkan sendiri dengan baris
// ringkasan di atasnya; dengan pohon, yang dibuka adalah yang sedang dilihat.
// Anak dimuat SAAT dibuka (bukan sekaligus di awal) — 2.590 objek tidak perlu
// ada di layar hanya untuk melihat ada berapa banyak.
//
// Tiga hal yang sengaja dibuat begini:
// 1. Fetch berjalan sebagai RANTAI batch kecil, bukan satu permintaan panjang.
//    Permintaan panjang mati di timeout dan hasilnya hilang; batch kecil bisa
//    dihentikan kapan saja dan dilanjutkan dari titik terakhir.
// 2. Tombol Stop menghentikan rantai SESUDAH batch berjalan selesai, bukan di
//    tengah panggilan SAP. Membatalkan di tengah hanya membuat status objek
//    tidak tercatat, lalu ditarik ulang percuma.
// 3. Bila backend menjawab `aborted` (autentikasi ditolak SAP), rantai berhenti
//    TOTAL dan pesannya ditampilkan apa adanya. Mengulang otomatis akan
//    mengunci akun RFC.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth, api as axiosApi } from '../context/AuthContext';

function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = (method === 'get' || method === 'delete')
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, []);
}

// Label tipe objek TADIR. Yang tidak terdaftar tampil apa adanya — lebih baik
// kode mentah daripada label karangan.
const OBJ_LABEL = {
  PROG: 'Program / Report', FUGR: 'Function Group', CLAS: 'Class', INTF: 'Interface',
  TABL: 'Table / Structure', VIEW: 'View', DTEL: 'Data Element', DOMA: 'Domain',
  TTYP: 'Table Type', TRAN: 'Transaction', MSAG: 'Message Class', SHLP: 'Search Help',
  ENQU: 'Lock Object',
};

const STATE_STYLE = {
  code:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  empty:   'bg-slate-50 text-slate-600 border-slate-200',
  missing: 'bg-amber-50 text-amber-700 border-amber-200',
  denied:  'bg-orange-50 text-orange-700 border-orange-200',
  error:   'bg-rose-50 text-rose-700 border-rose-200',
  auth:    'bg-rose-100 text-rose-800 border-rose-300',
};

function StateBadge({ state }) {
  if (!state) return <span className="px-2 py-0.5 text-xs rounded border bg-slate-50 text-slate-500 border-slate-200">not fetched</span>;
  const cls = STATE_STYLE[state] || 'bg-slate-50 text-slate-600 border-slate-200';
  return <span className={'px-2 py-0.5 text-xs rounded border ' + cls}>{state}</span>;
}

function Num({ v }) { return <span>{(v == null ? 0 : v).toLocaleString('en-US')}</span>; }

function Caret({ open }) {
  return (
    <span className={'inline-block w-3 text-slate-400 transition-transform ' + (open ? 'rotate-90' : '')}>
      {'▶'}
    </span>
  );
}

// Dipakai sebagai TAB di dalam halaman ABAP Source Viewer. Bila `connId`
// diberikan induknya, pemilih koneksi sendiri disembunyikan — dua pemilih
// koneksi di satu layar hanya membuat orang bertanya mana yang berlaku.
export default function SAPWorkbench({ connId: connIdProp, initialQ }) {
  const api = useApi();
  const { user } = useAuth();

  const [conns, setConns] = useState([]);
  const [ownConnId, setOwnConnId] = useState('');
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState(String(initialQ || '').toUpperCase());
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [progress, setProgress] = useState(null);

  // Pohon: tipe yang terbuka, isi tiap tipe, dan objek yang terbuka.
  const [openTypes, setOpenTypes] = useState({});
  const [typeData, setTypeData] = useState({});
  const [openObj, setOpenObj] = useState(null);
  const [search, setSearch] = useState(null);
  const [objDetail, setObjDetail] = useState(null);

  const connId = connIdProp || ownConnId;
  const PAGE = 50;
  // Ref, bukan state: rantai batch membacanya di tengah loop, dan state yang
  // dibaca di dalam closure loop selalu nilai lama.
  const stopRef = useRef(false);

  const conn = useMemo(
    () => conns.find(c => String(c.id) === String(connId)) || null, [conns, connId]);
  const sourceObjects = (summary && summary.source_objects) || ['PROG', 'FUGR', 'CLAS', 'INTF'];
  const perObject = (summary && summary.per_object) || [];

  useEffect(() => {
    let alive = true;
    api('/api/sap/source/connections')
      .then(d => {
        if (!alive) return;
        const list = (d && d.rows) || [];
        setConns(list);
        setOwnConnId(prev => prev || String((list.find(c => c.source_configured) || list[0] || {}).id || ''));
      })
      .catch(e => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api]);

  const loadSummary = useCallback(async (id) => {
    if (!id) return;
    try { setSummary(await api('/api/sap/workbench/summary?connectionId=' + id)); }
    catch (e) { setErr(e.message); }
  }, [api]);

  useEffect(() => { loadSummary(connId); }, [connId, loadSummary]);

  // Anak satu tipe. `append` dipakai tombol "Load more" supaya yang sudah
  // tampil tidak hilang saat halaman berikutnya datang.
  const loadType = useCallback(async (obj, off, append) => {
    if (!connId) return;
    setTypeData(d => Object.assign({}, d, { [obj]: Object.assign({ rows: [], total: 0 }, d[obj], { loading: true }) }));
    const p = new URLSearchParams({ connectionId: String(connId), object: obj,
      limit: String(PAGE), offset: String(off || 0) });
    if (q) p.set('q', q);
    try {
      const r = await api('/api/sap/workbench/objects?' + p.toString());
      setTypeData(d => {
        const prev = (append && d[obj] && d[obj].rows) || [];
        return Object.assign({}, d, { [obj]: {
          rows: prev.concat(r.rows || []), total: r.total || 0,
          offset: (off || 0), loading: false } });
      });
    } catch (e) {
      setErr(e.message);
      setTypeData(d => Object.assign({}, d, { [obj]: Object.assign({ rows: [], total: 0 }, d[obj], { loading: false }) }));
    }
  }, [api, connId, q]);

  const toggleType = useCallback((obj) => {
    setOpenTypes(o => {
      const next = Object.assign({}, o);
      if (next[obj]) { delete next[obj]; } else { next[obj] = true; }
      return next;
    });
    if (!openTypes[obj] && !(typeData[obj] && typeData[obj].rows && typeData[obj].rows.length)) {
      loadType(obj, 0, false);
    }
  }, [openTypes, typeData, loadType]);

  const toggleObj = useCallback(async (row) => {
    const key = row.object + '|' + row.obj_name;
    if (openObj === key) { setOpenObj(null); setObjDetail(null); return; }
    setOpenObj(key);
    setObjDetail({ loading: true });
    try {
      const p = new URLSearchParams({ connectionId: String(connId), object: row.object, name: row.obj_name });
      setObjDetail({ data: await api('/api/sap/workbench/source?' + p.toString()) });
    } catch (e) { setObjDetail({ error: e.message }); }
  }, [api, connId, openObj]);

  // Muat ulang isi tipe yang sedang terbuka (dipakai sesudah sync/fetch dan
  // sesudah kotak cari berubah).
  const refreshOpen = useCallback(() => {
    loadSummary(connId);
    Object.keys(openTypes).forEach(obj => loadType(obj, 0, false));
  }, [connId, openTypes, loadSummary, loadType]);

  // Pencarian LINTAS TIPE. Filter yang hanya berlaku di dalam tipe yang sedang
  // terbuka praktis tidak terlihat: kalau belum ada yang dibuka, mengetik tidak
  // mengubah apa pun. Jadi pencarian dijalankan tanpa filter tipe, hasilnya
  // dikelompokkan sendiri, dan tipe yang punya kecocokan langsung terbuka.
  const runSearch = useCallback(async () => {
    const term = q.trim();
    if (!term) { setSearch(null); refreshOpen(); return; }
    setErr(''); setBusy('search');
    try {
      const p = new URLSearchParams({ connectionId: String(connId), q: term, limit: '300' });
      const r = await api('/api/sap/workbench/objects?' + p.toString());
      setSearch({ rows: r.rows || [], total: r.total || 0, term: term });
      setOpenObj(null); setObjDetail(null);
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }, [api, connId, q, refreshOpen]);

  // Hasil pencarian dikelompokkan per tipe supaya bentuk pohonnya tetap sama.
  useEffect(() => {
    const term = q.trim();
    if (!term) { setSearch(null); return; }
    if (term.length < 2) return;
    const h = setTimeout(() => { runSearch(); }, 300);
    return () => clearTimeout(h);
  }, [q, runSearch]);

  const grouped = useMemo(() => {
    if (!search) return null;
    const m = {};
    (search.rows || []).forEach(r => { (m[r.object] = m[r.object] || []).push(r); });
    return m;
  }, [search]);

  // ── Aksi ────────────────────────────────────────────────────────────
  const syncTadir = useCallback(async () => {
    setErr(''); setNotice(''); setBusy('tadir');
    try {
      const d = await api('/api/sap/workbench/sync-tadir', {
        method: 'POST', body: JSON.stringify({ connectionId: Number(connId) }) });
      setNotice('TADIR synced: ' + d.total + ' objects');
      if ((d.warnings || []).length) setErr(d.warnings.join(' | '));
      refreshOpen();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }, [api, connId, refreshOpen]);

  const syncDd = useCallback(async () => {
    setErr(''); setNotice(''); setBusy('dd');
    try {
      const d = await api('/api/sap/workbench/sync-dd', {
        method: 'POST', body: JSON.stringify({ connectionId: Number(connId) }) });
      setNotice('Dictionary synced: ' + d.fields + ' fields in ' + d.tables +
        ' tables, ' + (d.doma || 0) + ' domains, ' + (d.dtel || 0) + ' data elements');
      refreshOpen();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }, [api, connId, refreshOpen]);

  // Rantai batch. Berhenti bila: pengguna menekan Stop, backend melaporkan
  // aborted, tidak ada sisa, atau satu batch tidak memproses apa pun (pengaman
  // supaya rantai tidak berputar selamanya bila ada objek yang tidak bisa
  // dipetakan ke nama program).
  const fetchSource = useCallback(async (opts) => {
    const o = opts || {};
    setErr(''); setNotice(''); setBusy('fetch');
    stopRef.current = false;
    let done = 0, lines = 0, last = null;
    try {
      for (;;) {
        const body = { connectionId: Number(connId), limit: 50 };
        if (o.object) body.object = o.object;
        if (o.retryFailed) body.retryFailed = true;
        last = await api('/api/sap/workbench/fetch-source', {
          method: 'POST', body: JSON.stringify(body) });
        done += last.processed || 0;
        lines += last.saved_lines || 0;
        setProgress({ done: done, lines: lines, remaining: last.remaining });
        if (last.aborted) { setErr(last.aborted); break; }
        if (!last.remaining || !last.processed || o.once || stopRef.current) break;
      }
      if (last && !last.aborted) {
        setNotice('Fetched ' + done + ' objects, ' + lines.toLocaleString('en-US') +
          ' source lines saved. Remaining: ' + (last.remaining || 0) +
          (stopRef.current ? ' (stopped)' : ''));
      }
      refreshOpen();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }, [api, connId, refreshOpen]);

  const srcRows = perObject.filter(p => sourceObjects.indexOf(p.object) >= 0);
  const fetchedSource = srcRows.reduce((a, p) => a + (p.code || 0) + (p.empty || 0), 0);
  const pendingSource = srcRows.reduce((a, p) => a + (p.pending || 0), 0);
  const failedSource = srcRows.reduce((a, p) => a + (p.error || 0) + (p.denied || 0) + (p.missing || 0), 0);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Inventory of custom (Z*/Y*) repository objects from TADIR, with bulk source retrieval.
        Signed in as {user ? user.username : '—'}.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {!connIdProp && (
          <select className="border rounded px-2 py-1 text-sm"
                  value={connId} onChange={e => { setOwnConnId(e.target.value); setTypeData({}); }}>
            {conns.map(c => (
              <option key={c.id} value={c.id}>
                {c.sid} / {c.client} — {c.host}{c.source_configured ? '' : ' (no source path)'}
              </option>
            ))}
          </select>
        )}
        <button className="px-3 py-1 text-sm rounded bg-sky-600 text-white disabled:opacity-50"
                disabled={!!busy || !connId} onClick={syncTadir}>
          {busy === 'tadir' ? 'Syncing…' : 'Sync TADIR'}
        </button>
        <button className="px-3 py-1 text-sm rounded bg-indigo-600 text-white disabled:opacity-50"
                disabled={!!busy || !connId} onClick={syncDd}>
          {busy === 'dd' ? 'Syncing…' : 'Sync Dictionary'}
        </button>
        <button className="px-3 py-1 text-sm rounded bg-emerald-600 text-white disabled:opacity-50"
                disabled={!!busy || !connId || !(conn && conn.source_configured)}
                onClick={() => fetchSource({})}>
          {busy === 'fetch' ? 'Fetching…' : 'Fetch all source'}
        </button>
        <button className="px-3 py-1 text-sm rounded border border-slate-300 disabled:opacity-50"
                disabled={!!busy || !connId} onClick={() => fetchSource({ retryFailed: true, once: true })}>
          Retry failed (one batch)
        </button>
        {busy === 'fetch' && (
          <button className="px-3 py-1 text-sm rounded bg-rose-600 text-white"
                  onClick={() => { stopRef.current = true; }}>Stop</button>
        )}
        <button className="px-3 py-1 text-sm rounded border border-slate-300"
                onClick={refreshOpen} disabled={!!busy}>Refresh</button>
      </div>

      <div className="flex items-center gap-2">
        <input className="border rounded px-3 py-2 text-sm flex-1 max-w-xl"
               placeholder="Type to search object name or package across all types — e.g. ZMM, ZD01, MIGO"
               value={q}
               onChange={e => setQ(e.target.value.toUpperCase())}
               onKeyDown={e => { if (e.key === 'Enter') runSearch(); }} />
        {busy === 'search' && <span className="text-sm text-slate-500">Searching…</span>}
        {search && (
          <button className="px-3 py-2 text-sm rounded border border-slate-300"
                  onClick={() => { setQ(''); setSearch(null); }}>Clear</button>
        )}
        {search && (
          <span className="text-sm text-slate-500">
            <Num v={search.total} /> match{search.total === 1 ? '' : 'es'} for “{search.term}”
            {search.total > (search.rows || []).length ? ' (showing first ' + (search.rows || []).length + ')' : ''}
          </span>
        )}
      </div>

      {conn && !conn.source_configured && (
        <div className="text-sm rounded border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2">
          This connection has no ZRFC_READ_SOURCE path configured, so source retrieval is unavailable.
          The TADIR inventory still works.
        </div>
      )}
      {err && <div className="text-sm rounded border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2">{err}</div>}
      {notice && <div className="text-sm rounded border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2">{notice}</div>}
      {busy === 'fetch' && progress && (
        <div className="text-sm rounded border border-sky-200 bg-sky-50 text-sky-800 px-3 py-2">
          Fetching… {progress.done} objects done, <Num v={progress.lines} /> lines saved, {progress.remaining} remaining.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">Objects in inventory</div>
          <div className="text-2xl font-semibold text-slate-800">
            <Num v={perObject.reduce((a, p) => a + (p.total || 0), 0)} />
          </div>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">Source fetched</div>
          <div className="text-2xl font-semibold text-emerald-700"><Num v={fetchedSource} /></div>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">Source pending</div>
          <div className="text-2xl font-semibold text-sky-700"><Num v={pendingSource} /></div>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">Failed / no source</div>
          <div className="text-2xl font-semibold text-rose-700"><Num v={failedSource} /></div>
        </div>
      </div>

      {/* Pohon: tipe objek → objek → isi */}
      <div className="rounded border border-slate-200 bg-white divide-y divide-slate-100">
        {(grouped ? perObject.filter(p => grouped[p.object]) : perObject).map(p => {
          // Saat mencari, tipe yang punya kecocokan selalu terbuka — hasil
          // pencarian yang masih harus diklik satu-satu bukan hasil pencarian.
          const open = grouped ? true : !!openTypes[p.object];
          const td = grouped
            ? { rows: grouped[p.object], total: grouped[p.object].length, loading: false }
            : (typeData[p.object] || {});
          const isSrc = sourceObjects.indexOf(p.object) >= 0;
          const failed = (p.error || 0) + (p.denied || 0) + (p.missing || 0);
          return (
            <div key={p.object}>
              <div className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                <button className="flex items-center gap-2 flex-1 text-left"
                        onClick={() => toggleType(p.object)}>
                  <Caret open={open} />
                  <span className="font-mono text-xs text-slate-500 w-11">{p.object}</span>
                  <span className="text-sm text-slate-800">{OBJ_LABEL[p.object] || p.object}</span>
                  {!isSrc && <span className="text-xs text-slate-400">no ABAP source</span>}
                </button>
                <span className="text-xs text-slate-500">
                  <Num v={p.total} /> objects
                  {isSrc && <span className="text-emerald-700"> · <Num v={p.code} /> with code</span>}
                  {isSrc && !!p.pending && <span className="text-sky-700"> · <Num v={p.pending} /> pending</span>}
                  {isSrc && !!failed && <span className="text-rose-700"> · <Num v={failed} /> failed</span>}
                </span>
                {isSrc && !!p.pending && (
                  <button className="text-xs px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 disabled:opacity-40"
                          disabled={!!busy || !(conn && conn.source_configured)}
                          onClick={() => fetchSource({ object: p.object })}>Fetch</button>
                )}
              </div>

              {open && (
                <div className="bg-slate-50 border-t border-slate-100">
                  {td.loading && <div className="pl-10 py-2 text-sm text-slate-500">Loading…</div>}
                  {!td.loading && !(td.rows || []).length && (
                    <div className="pl-10 py-2 text-sm text-slate-500">No objects match this filter.</div>
                  )}
                  {(td.rows || []).map(r => {
                    const key = r.object + '|' + r.obj_name;
                    const oOpen = openObj === key;
                    return (
                      <div key={key} className="border-t border-slate-100">
                        <div className="flex items-center gap-2 pl-10 pr-3 py-1.5 hover:bg-white cursor-pointer"
                             onClick={() => toggleObj(r)}>
                          <Caret open={oOpen} />
                          <span className="font-mono text-sm flex-1 text-slate-800">{r.obj_name}</span>
                          <span className="text-xs text-slate-500 w-28 truncate">{r.devclass}</span>
                          <span className="text-xs text-slate-500 w-24 truncate">{r.author}</span>
                          <span className="text-xs text-slate-400 w-20">{r.created_on}</span>
                          {isSrc
                            ? <StateBadge state={r.state} />
                            : <span className="text-xs text-slate-400">no source</span>}
                          <span className="text-xs text-slate-500 w-14 text-right"><Num v={r.lines} /></span>
                        </div>
                        {oOpen && (
                          <div className="pl-10 pr-3 pb-3 bg-white">
                            {objDetail && objDetail.loading && <div className="text-sm text-slate-500 py-2">Loading…</div>}
                            {objDetail && objDetail.error && <div className="text-sm text-rose-700 py-2">{objDetail.error}</div>}
                            {objDetail && objDetail.data && <DetailBody d={objDetail.data} />}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(td.rows || []).length < (td.total || 0) && (
                    <div className="pl-10 py-2 border-t border-slate-100">
                      <button className="text-xs px-2 py-1 rounded border border-slate-300"
                              onClick={() => loadType(p.object, (td.rows || []).length, true)}>
                        Load more — showing {(td.rows || []).length} of {td.total}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {grouped && !Object.keys(grouped).length && (
          <div className="px-3 py-4 text-sm text-slate-500">
            No object matches “{search.term}” in this connection.
          </div>
        )}
        {!perObject.length && (
          <div className="px-3 py-4 text-sm text-slate-500">
            No inventory yet for this connection. Press “Sync TADIR” to build it.
          </div>
        )}
      </div>

      {summary && summary.dd && (
        <div className="text-sm text-slate-500">
          Dictionary stored: <Num v={summary.dd.fields} /> fields across <Num v={summary.dd.tables} /> tables/views.
        </div>
      )}

      {summary && (summary.runs || []).length > 0 && (
        <div className="text-xs text-slate-500">
          <div className="font-medium text-slate-600 mb-1">Recent runs</div>
          {summary.runs.slice(0, 5).map((r, i) => (
            <div key={i} className="font-mono">
              {String(r.finished_at || '').slice(0, 19).replace('T', ' ')} · {r.kind} · {r.error ? ('ERROR: ' + r.error) : r.detail}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Isi satu objek. Bentuknya berbeda per tipe, dan itu memang keadaan di SAP:
// program/kelas punya BARIS KODE (bisa terpecah ke banyak include), tabel punya
// FIELD, domain/data element hanya punya metadata. Menyeragamkannya jadi satu
// tampilan berarti menyembunyikan perbedaan yang justru penting.
function DetailBody({ d }) {
  if (d.line_count && (d.parts || []).length) {
    return (
      <div className="space-y-3 pt-2">
        <div className="text-xs text-slate-500">
          {d.parts.length} program(s)/include(s) · {d.line_count} lines
          {d.pool && d.pool !== d.obj_name ? ' · pool: ' + d.pool : ''}
        </div>
        {d.parts.map(part => (
          <div key={part.progname}>
            <div className="text-xs font-mono text-slate-600 mb-1">
              {part.progname} · {part.lines.length} lines
            </div>
            <pre className="text-xs font-mono bg-slate-900 text-slate-100 rounded p-3 overflow-auto max-h-96">
{part.lines.map(l => String(l.lineno).padStart(5, ' ') + '  ' + l.content).join('\n')}
            </pre>
          </div>
        ))}
      </div>
    );
  }
  if (d.kind === 'dd_fields' && (d.fields || []).length) {
    return (
      <div className="overflow-x-auto rounded border border-slate-200 mt-2">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-1.5">#</th>
              <th className="text-left px-3 py-1.5">Field</th>
              <th className="text-left px-3 py-1.5">Key</th>
              <th className="text-left px-3 py-1.5">Data element</th>
              <th className="text-left px-3 py-1.5">Type</th>
              <th className="text-right px-3 py-1.5">Length</th>
              <th className="text-left px-3 py-1.5">Check table</th>
            </tr>
          </thead>
          <tbody>
            {d.fields.map(f => (
              <tr key={f.fieldname} className="border-t border-slate-100">
                <td className="px-3 py-1 text-slate-400">{f.position}</td>
                <td className="px-3 py-1 font-mono">{f.fieldname}</td>
                <td className="px-3 py-1 text-slate-500">{f.keyflag === 'X' ? 'key' : ''}</td>
                <td className="px-3 py-1 font-mono text-slate-600">{f.rollname}</td>
                <td className="px-3 py-1">{f.datatype}</td>
                <td className="px-3 py-1 text-right">{f.leng}</td>
                <td className="px-3 py-1 font-mono text-slate-600">{f.checktable}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (d.kind === 'dd_meta' && d.meta) {
    return (
      <table className="text-sm mt-2">
        <tbody>
          {Object.keys(d.meta).map(k => (
            <tr key={k}>
              <td className="pr-6 py-0.5 text-slate-500">{k}</td>
              <td className="py-0.5 font-mono">{d.meta[k] == null ? '' : String(d.meta[k])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <div className="text-sm text-slate-500 py-2">
      {d.note || 'No stored content for this object.'}
    </div>
  );
}
